import React, { useState, useEffect, useRef, useCallback } from "react";
import * as R from "ramda";
// import { nanoid } from "nanoid";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { appAuth, logError } from "api/accounts-customer";
import {
  isNilOrEmpty,
  decodeJWT,
  inIframe,
  usePostMessage,
  useSessionJWT,
  useSessionCtx,
  useAnalytics,
} from "utils";
import { FullPageSpinner } from "components/Spinner";
import EmptyState from "components/EmptyState";
import useResizeObserver from "use-resize-observer/polyfilled";
import TiliaAPIError from "api/TiliaAPIError";
import * as fraudApi from "api/fraud";
// import { CHECKOUT } from "flows/constants";

const flowMap = {
  addcard: "/addcard",
  addpm: "/addpm",
  deposit: "/deposit",
  kyc: "/kyc",
  kyc2: "/kyc2",
  payout: "/payout",
  "payout-full": "/payout-full",
  payoutv2: "/payoutv2",
  gdpr: "/gdpr",
  pm: "/pm",
  "payment-selection": "/payment-selection",
  "payment-method-management": "/payment-method-management",
  purchase: "/purchase",
  checkout: "/checkout",
  txnhistory: "/txnhistory",
  txndetails: "/txndetails",
  tos: "/tos",
  error: "/error",
  "session-id": "/session-id",
};

export const getFlowDestination = (flow = "") => {
  const key = R.toLower(flow);
  const keys = R.keys(flowMap);
  if (!R.contains(key, keys)) {
    console.error("Invalid flow:", key);
    return flowMap["error"];
  }
  return flowMap[key];
};

const Redirect = ({ flow }) => {
  const history = useHistory();
  useEffect(() => {
    history.replace(getFlowDestination(flow));
  }, [history, flow]);
  return null;
};

const isInvalidToken = (err) => {
  // If the user has an expired or otherwise unknown nonce key
  return err instanceof TiliaAPIError && err.status === 404;
};

const pre = "[AppAuth]";
export const AppAuth = () => {
  const [isReady, setIsReady] = useState(false);
  const [authError, setAuthError] = useState(null);
  const { sessionJWT, setSessionJWT } = useSessionJWT();
  const { sessionCtx, setSessionCtx, debug, setSessionId } = useSessionCtx();
  const location = useLocation();
  const { token: tokenInUrl } = useParams();
  const qsParams = new URLSearchParams(location.search);
  const [flow] = useState(qsParams.get("flow"));
  const { postOnError, postOnEvent } = usePostMessage(flow, sessionCtx?.intCtx);
  const analytics = useAnalytics();
  const trackedJwt = useRef(null); // used to track if authentication changes so we can fire analytics
  const viewEventFired = useRef(false);
  const authCheckUseEffectHasBeenCalled = useRef(false);

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("AppAuth", "AppAuth_Viewed", { flow });
      viewEventFired.current = true;
    }
  }, [analytics, flow, viewEventFired]);

  useEffect(() => {
    /**
     * useEffect to track user authentication
     * In general, this will fire once if there's no
     * current sessionJWT.  It is possible for this to
     * fire twice if there is an existing sessionJWT
     * in sessionStorage (this will often happen with
     * Fake Integrator) which will be replaced when we
     * process the new one.
     */
    if (sessionCtx && sessionJWT && sessionJWT !== trackedJwt.current) {
      trackedJwt.current = sessionJWT;
      analytics.identify(sessionJWT, sessionCtx, debug, { flow });
    }
  }, [analytics, sessionJWT, sessionCtx, flow, debug]);

  const postDim = ({ height }) => {
    if (!inIframe() || !height) return;
    const dim = { height };
    debug(pre, "posting resize:", dim);
    postOnEvent({ type: "resize", dim });
  };

  const { ref: flowRef } = useResizeObserver({ onResize: postDim });

  const handleAuth = useCallback(async () => {
    let appAuthResponse;
    try {
      appAuthResponse = await appAuth(tokenInUrl);
      const decoded = decodeJWT(appAuthResponse?.payload?.jwt || "");
      if (isNilOrEmpty(appAuthResponse?.payload) || !decoded?.account_id) {
        throw new Error("Unable to authenticate.");
      }
      setSessionJWT(appAuthResponse, tokenInUrl);

      /*--- HOLDING THE CODE BELOW IN CASE WE NEED TO BRING BACK THE CUSTOM SESSION ID FOR CHECKOUT FLOW ---*/
      // TODO: Move this into a separate useEffect that relies on sessionJWT being set before
      //       we generate a session ID?
      // TODO: Move this into redis as a key on flows?
      // let newSessionId;
      // if (flow.toUpperCase() === CHECKOUT) {
      //   newSessionId = nanoid(32);
      //   debug(pre, "Generated new session ID on the client");
      // } else {
      //   const session = await fraudApi.createSessionV3(
      //     appAuthResponse.payload,
      //     sessionData.account_id
      //   );
      //   newSessionId = session?.payload?.session_id;
      //   console.log("--> fraudApi.createSessionV3, newSessionId:", newSessionId);
      //   debug(pre, "Grabbed new session ID from fraud API");
      // }
      /*--------------*/

      /*--- ALWAYS PULLING SESSION ID FROM FRAUD API ---*/
      const session = await fraudApi.createSessionV3(
        appAuthResponse.payload,
        decoded.account_id,
        flow
      );
      const newSessionId = session?.payload?.session_id;
      debug(pre, "Grabbed new session ID from fraud API");
      if (!newSessionId) {
        debug(pre, "Session ID is falsy. Throwing!");
        throw new Error("Unable to generate session ID.");
      }
      /*--------------*/

      setSessionId(newSessionId);
      setSessionCtx(location);
      setIsReady(true);
    } catch (err) {
      logError({}, {
        flowLoc: "APP_AUTH",
        error: err.message,
        tokenInUrl,
      });
      setAuthError(err);
      debug(pre, err);
      if (err && inIframe()) {
        const message = isInvalidToken(err)
          ? "Invalid link.  This is usually the result of expired data."
          : err.message || "Unknown error occured";
        postOnError({ message });
      }
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    setSessionJWT,
    debug,
    setSessionId,
    setSessionCtx,
    setIsReady,
    setAuthError,
    postOnError,
    location,
    tokenInUrl,
  ]); // disabled exhaustive-deps since we're using 'flow' initial value without having it the array

  /**
   * useEffect to handle user authentication token
   * we either have it locally in storage (would have same nonce token found in uri),
   * or we have to get a new one using the new auth token.
   */
  useEffect(() => {
    if (authCheckUseEffectHasBeenCalled.current) return; // This useEffect should only be called once!  Either we set 'isReady' to true here, or we do it in handleAuth function.
    const storedNonceToken = decodeJWT(sessionJWT?.jwt)?.token_key;

    const isNewNonceToken = tokenInUrl !== storedNonceToken;
    if (isNewNonceToken) {
      handleAuth(); // this is an async function, but we don't have to wait for it as it will update either the isReady state or an error
    } else {
      setSessionCtx(location);
      setIsReady(true);
    }
    authCheckUseEffectHasBeenCalled.current = true;
  }, [
    handleAuth,
    sessionJWT, 
    tokenInUrl, 
    location, 
    setSessionCtx, 
    setIsReady
  ]);

  let nonRedirectContent = null;
  const error = R.equals(R.prop("status", sessionJWT), "Failure");

  if (authError || error) {
    const e = authError || error;
    if (isInvalidToken(e)) {
      nonRedirectContent = (
        <EmptyState
          title="Invalid link"
          description="This is usually the result of expired data."
        />
      );
    } else {
      nonRedirectContent = <EmptyState error={e} />;
    }
  } else if (!isReady || !R.prop("status", sessionJWT)) {
    nonRedirectContent = <FullPageSpinner id="APPAUTH" />;
  }

  if (nonRedirectContent) {
    return <div ref={flowRef}>{nonRedirectContent}</div>;
  }

  return <Redirect flow={sessionCtx.flow} />;
};
